import {
  Box,
  Button,
  Chip,
  Popover,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { DateTime } from "luxon";
import { utcToZonedTime } from "date-fns-tz";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import QCModal from "../../components/QCModal";
import moment from "moment";

export const STATUS_MAP = {
  0: "Inactive",
  1: "Match Created",
  2: "Video Uploaded",
  3: "Analysis Done",
  4: "Resources Assigned",
  5: "Analysis In-Progress",
  6: "Review In-Progress",
  7: "COMPLETED",
  8: "Mark For Review",
  9: "Issue Found",
};


function getStatusConfig(status) {
  switch (status) {
    case 0: return { text: "Inactive", color: "#6B7280" };
    case 1: return { text: "Created", color: "#6B7280" };
    case 2: return { text: "Video Uploaded", color: "#3B82F6" };
    case 3: return { text: "Analysis Done", color: "#10B981" };
    case 4: return { text: "Assigned", color: "#6366F1" };
    case 5: return { text: "In Progress", color: "#F59E0B" };
    case 6: return { text: "Review In Progress", color: "#8B5CF6" };
    case 7: return { text: "Completed", color: "#059669" };
    case 8: return { text: "Marked for Review", color: "#EC4899" };
    case 9: return { text: "Issue Found", color: "#EF4444" };
    default: return { text: "Unknown", color: "#6B7280" };
  }
}

const chipSx = {
  borderRadius: "4px",
  fontSize: "11px",
  height: "22px",
  color: "var(--text-1)",
  backgroundColor: "var(--surface-2)",
  border: "1px solid var(--border-1)",
  "& .MuiChip-label": { px: "6px" },
};

const ChipExpandList = ({ items, labelFn, emptyLabel }) => {
  const [anchor, setAnchor] = useState(null);

  if (!items || items.length === 0) {
    return (
      <Chip
        label={emptyLabel}
        size="small"
        sx={{ ...chipSx, color: "var(--text-2)", fontStyle: "italic" }}
      />
    );
  }

  const first = items[0];
  const rest = items.slice(1);

  return (
    <Stack direction="row" alignItems="center" gap={0.5} flexWrap="nowrap">
      <Chip label={labelFn(first)} size="small" sx={chipSx} />
      {rest.length > 0 && (
        <>
          <Chip
            label={`+${rest.length}`}
            size="small"
            onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
            sx={{
              ...chipSx,
              cursor: "pointer",
              backgroundColor: "rgba(232,108,0,0.1)",
              color: "rgba(232,108,0,1)",
              borderColor: "rgba(232,108,0,0.3)",
              fontWeight: 700,
              "&:hover": { backgroundColor: "rgba(232,108,0,0.18)" },
            }}
          />
          <Popover
            open={Boolean(anchor)}
            anchorEl={anchor}
            onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                p: 1,
                borderRadius: "8px",
                border: "1px solid var(--border-1)",
                backgroundColor: "var(--surface-1)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                maxWidth: 260,
              },
            }}
          >
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {rest.map((item, i) => (
                <Chip key={i} label={labelFn(item)} size="small" sx={chipSx} />
              ))}
            </Stack>
          </Popover>
        </>
      )}
    </Stack>
  );
};

export const MatchCard = ({ data, onEditPress, viewRef }) => {
  const [isQcModalOpen, setIsQcModalOpen] = useState(false);
  const navHistory = useHistory();
  if (!data) return null;

  const formatTimeFromSeconds = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hrs}hrs ${mins}mins`;
  };

  const formatTimeFromSecondsToMinutes = (seconds) => {
    const mins = Math.floor(seconds / 60);
    return `${mins}mins`;
  };

  const getTatBadgeSx = (seconds) => {
    if (!seconds) {
      return null;
    }

    const hours = seconds / 3600;
    if (hours > 48) {
      return {
        color: "#fff5f5",
        backgroundColor: "#991b1b",
        borderColor: "#dc2626",
      };
    }

    if (hours >= 24) {
      return {
        color: "var(--tat-warning-text, #7f1d1d)",
        backgroundColor: "var(--tat-warning-bg, #fecaca)",
        borderColor: "#ef4444",
      };
    }

    return {
      color: "var(--tat-ok-text, #14532d)",
      backgroundColor: "var(--tat-ok-bg, #dcfce7)",
      borderColor: "#22c55e",
    };
  };

  const renderTatValue = (seconds) => {
    const badgeSx = getTatBadgeSx(seconds);
    if (!badgeSx) {
      return "NA";
    }

    return (
      <Box
        sx={{
          ...badgeSx,
          border: "1px solid",
          borderRadius: "4px",
          display: "inline-flex",
          justifyContent: "center",
          minWidth: "74px",
          px: 1,
          py: 0.5,
          fontSize: "12px",
          fontWeight: 600,
          fontFamily: "Poppins",
          lineHeight: "120%",
          textAlign: "center",
        }}
      >
        {formatTimeFromSeconds(seconds)}
      </Box>
    );
  };

  const formatDateAndTime = (dateWithTime) => {
    const _dateWithTime = dateWithTime?.replace(" ", "T");
    if (_dateWithTime) {
      const utcTime = DateTime.fromISO(_dateWithTime, { zone: "utc" });
      const istTime = utcToZonedTime(utcTime.toJSDate(), "Asia/Kolkata");
      return format(istTime, "PPpp");
    } else {
      return "NA";
    }
  };

  const statusConfig = getStatusConfig(data?.status);

  const cellSx = {
    fontSize: "12px",
    color: "var(--text-1)",
    fontWeight: "500",
    lineHeight: "1.4",
    py: "10px",
    verticalAlign: "middle",
  };

  return (
    <>
      <QCModal
        setIsQcModalOpen={setIsQcModalOpen}
        modalIsOpen={isQcModalOpen}
        matchId={data.id}
      />
      <TableRow ref={viewRef} hover sx={{ "&:hover": { backgroundColor: "var(--surface-2) !important" } }}>

        {/* ID */}
        <TableCell
          component="th"
          scope="row"
          sx={{
            ...cellSx,
            position: "sticky",
            left: 0,
            zIndex: 888,
            fontWeight: "700",
            backgroundColor: "var(--surface-1)",
            minWidth: "70px",
            borderRight: "1px solid var(--border-1)",
          }}
        >
          {data.id}
        </TableCell>

        {/* Match Details */}
        <TableCell sx={{ ...cellSx, textAlign: "center", minWidth: "140px" }}>
          <Typography sx={{ fontSize: "12px", fontWeight: 600, lineHeight: 1.3, color: "var(--text-1)" }}>
            {data?.team_a_details?.name}
          </Typography>
          <Typography sx={{ fontSize: "11px", color: "#1976d2", fontWeight: 600, lineHeight: 1.2 }}>
            vs
          </Typography>
          <Typography sx={{ fontSize: "12px", fontWeight: 600, lineHeight: 1.3, color: "var(--text-1)" }}>
            {data?.team_b_details?.name}
          </Typography>
        </TableCell>

        {/* Analysts */}
        <TableCell sx={{ ...cellSx, textAlign: "center", minWidth: "120px" }}>
          <ChipExpandList
            items={data?.analyst_details}
            labelFn={(item) => item?.name}
            emptyLabel="No Analyst"
          />
        </TableCell>

        {/* Video Uploaded */}
        <TableCell sx={{ ...cellSx, minWidth: "110px" }}>
          {data?.video_upload_date
            ? moment(data?.video_upload_date).format("MMM DD, YYYY HH:mm")
            : "NA"}
        </TableCell>

        {/* Assign Date */}
        <TableCell sx={{ ...cellSx, minWidth: "110px" }}>
          {data?.analysis_sense_data?.assigned_date
            ? moment(data?.analysis_sense_data?.assigned_date).format("MMM DD, YYYY HH:mm")
            : "NA"}
        </TableCell>

        {/* Start Time */}
        <TableCell sx={{ ...cellSx, minWidth: "110px" }}>
          {data?.analysis_sense_data?.analysis_start_time
            ? moment(data?.analysis_sense_data?.analysis_start_time).format("MMM DD, YYYY HH:mm")
            : "NA"}
        </TableCell>

        {/* Progress */}
        <TableCell sx={{ ...cellSx, textAlign: "center", minWidth: "90px" }}>
          <Box
            sx={{
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "center",
              px: "6px",
              py: "2px",
              border: "1px solid var(--border-1)",
              bgcolor: "var(--surface-2)",
              borderRadius: "4px",
              minWidth: 44,
            }}
          >
            <Typography sx={{ fontSize: "12px", fontWeight: 700, lineHeight: 1.2, color: "var(--text-1)" }}>
              {data?.progress != null ? `${data?.progress}%` : "NA"}
            </Typography>
            {(data?.analysis_sense_data?.analysis_progress || data?.analysis_sense_data?.current_analysis_time) && (
              <Typography sx={{ fontSize: "10px", color: "var(--text-2)", lineHeight: 1.2 }}>
                {data.analysis_sense_data.analysis_progress || data.analysis_sense_data.current_analysis_time}
              </Typography>
            )}
          </Box>
        </TableCell>

        {/* End Time */}
        <TableCell sx={{ ...cellSx, minWidth: "110px" }}>
          {data?.analysis_sense_data?.analysis_end_time
            ? moment(data?.analysis_sense_data?.analysis_end_time)?.format(
              "MMM DD, YYYY[\n]hh:mm A"
            )
            : "NA"}
        </TableCell>
        <TableCell
          sx={{
            fontSize: { lg: "12px", md: "12px", xs: "12px" },
            color: "var(--text-1)",
            fontWeight: "500",
            fontFamily: "Poppins",
            textAlign: "right",
            lineHeight: "120%",
            minWidth: "100px",
          }}
        >
          {data?.analysis_sense_data?.full_completion_time
            ? moment(data?.analysis_sense_data?.full_completion_time)?.format(
              "MMM DD, YYYY[\n]hh:mm A"
            )
            : "NA"}
        </TableCell>
        {/* <TableCell
          sx={{
            fontSize: { lg: "12px", md: "12px", xs: "12px" },
            color: "var(--text-1)",
            fontWeight: "500",
            fontFamily: "Poppins",
            textAlign: "right",
            lineHeight: "120%",
            minWidth: "100px",
          }}
        >
          {data?.analysis_sense_data?.current_analysis_time
            ? moment(data?.analysis_sense_data?.current_analysis_time)?.format(
              "MMM DD, YYYY[\n]hh:mm A"
            )
            : "NA"}
        </TableCell>
        <TableCell
          sx={{
            fontSize: { lg: "12px", md: "12px", xs: "12px" },
            color: "var(--text-1)",
            fontWeight: "500",
            fontFamily: "Poppins",
            textAlign: "right",
            lineHeight: "120%",
            minWidth: "100px",
          }}
        >
          {data?.analysis_sense_data?.due_date
            ? moment(data?.analysis_sense_data?.due_date).format(
              "MMM DD, YYYY[\n]hh:mm A"
            )
            : "NA"}
        </TableCell> */}

        {/* Last Tagged */}
        <TableCell sx={{ ...cellSx, minWidth: "110px" }}>
          {data?.analysis_sense_data?.current_analysis_time
            ? moment(data?.analysis_sense_data?.current_analysis_time).format("MMM DD, YYYY HH:mm")
            : "NA"}
        </TableCell>

        {/* Due Date */}
        <TableCell sx={{ ...cellSx, minWidth: "110px" }}>
          {data?.analysis_sense_data?.due_date
            ? moment(data?.analysis_sense_data?.due_date).format("MMM DD, YYYY HH:mm")
            : "NA"}
        </TableCell>

        {/* Status */}
        <TableCell sx={{ ...cellSx, textAlign: "center", minWidth: "120px" }}>
          <Chip
            label={statusConfig.text}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "11px",
              height: "24px",
              backgroundColor: `${statusConfig.color}18`,
              color: statusConfig.color,
              border: `1px solid ${statusConfig.color}40`,
              "& .MuiChip-label": { px: 1.5 },
            }}
          />
        </TableCell>

        {/* Reviewers */}
        <TableCell sx={{ ...cellSx, textAlign: "center", minWidth: "120px" }}>
          <ChipExpandList
            items={data?.reviewer_details}
            labelFn={(item) => item?.name}
            emptyLabel="No Reviewer"
          />
        </TableCell>

        {/* Account */}
        <TableCell sx={{ ...cellSx, textAlign: "center", minWidth: "120px" }}>
          <ChipExpandList
            items={data?.organizer_details}
            labelFn={(item) => `${item?.id} - ${item?.name}`}
            emptyLabel="NA"
          />
        </TableCell>

        {/* Screen Time */}
        <TableCell sx={{ ...cellSx, textAlign: "center", minWidth: "100px" }}>
          {data?.analyst_screen_time ? formatTimeFromSeconds(data?.analyst_screen_time) : "NA"}
        </TableCell>

        {/* Analysis TAT */}
        <TableCell sx={{ ...cellSx, textAlign: "center", minWidth: "100px" }}>
          {data?.analysis_tat ? formatTimeFromSeconds(data?.analysis_tat) : "NA"}
        </TableCell>

        {/* Reviewer TAT */}
        <TableCell sx={{ ...cellSx, textAlign: "center", minWidth: "100px" }}>
          {data?.reviewer_tat ? formatTimeFromSeconds(data?.reviewer_tat) : "NA"}
        </TableCell>
        <TableCell
          sx={{
            fontSize: {
              lg: "12px",
              md: "12px",
              xs: "12px",
              color: "var(--text-1)",
              fontWeight: "500",
              fontFamily: "Poppins",
              textAlign: "center",
              lineHeight: "120%",
            },
          }}
        >
          {renderTatValue(data?.overall_tat)}
        </TableCell>
        <TableCell
          sx={{
            fontSize: {
              lg: "12px",
              md: "12px",
              xs: "12px",
              color: "var(--text-1)",
              fontWeight: "500",
              fontFamily: "Poppins",
              textAlign: "center",
              lineHeight: "120%",
            },
          }}
        >
          {renderTatValue(data?.client_tat)}
        </TableCell>

        {/* Game Time */}
        <TableCell sx={{ ...cellSx, textAlign: "center", minWidth: "90px" }}>
          {data?.game_time ? `${data?.game_time}mins` : "NA"}
        </TableCell>

        {/* Total Time */}
        <TableCell sx={{ ...cellSx, textAlign: "center", minWidth: "90px" }}>
          {data?.total_game_time ? formatTimeFromSecondsToMinutes(data.total_game_time) : "NA"}
        </TableCell>

        {/* Edit Action */}
        <TableCell sx={{ ...cellSx, textAlign: "center" }}>
          <Button
            onClick={onEditPress}
            variant="outlined"
            size="small"
            sx={{
              textTransform: "none",
              fontSize: "11px",
              fontWeight: 600,
              borderRadius: "6px",
              height: "26px",
              px: "10px",
              borderColor: "rgba(232,108,0,0.5)",
              color: "rgba(232,108,0,1)",
              "&:hover": {
                borderColor: "rgba(232,108,0,1)",
                backgroundColor: "rgba(232,108,0,0.08)",
              },
            }}
          >
            Edit
          </Button>
        </TableCell>

        {/* QC Action */}
        <TableCell sx={{ ...cellSx, textAlign: "center" }}>
          <Button
            onClick={() => setIsQcModalOpen(true)}
            variant="outlined"
            size="small"
            sx={{
              textTransform: "none",
              fontSize: "11px",
              fontWeight: 600,
              borderRadius: "6px",
              height: "26px",
              px: "10px",
              borderColor: "var(--border-1)",
              color: "var(--text-2)",
              "&:hover": {
                borderColor: "var(--text-1)",
                backgroundColor: "var(--surface-2)",
              },
            }}
          >
            QC
          </Button>
        </TableCell>

        {/* History — navigate to /management/history/:matchId */}
        <TableCell
          sx={{
            fontSize: { lg: "12px", md: "12px", xs: "12px" },
            color: "var(--text-1)",
            fontWeight: "500",
            fontFamily: "Poppins",
            textAlign: "center",
            lineHeight: "120%",
          }}
        >
          <Tooltip
            title={
              data?.analyst_details?.length
                ? `View cloud history for ${data.analyst_details.length} analyst${data.analyst_details.length !== 1 ? "s" : ""}`
                : "No analysts assigned"
            }
          >
            <span>
              <Button
                onClick={() => navHistory.push(`/management/history/${data.id}`, { match: data })}
                variant="outlined"
                size="small"
                disabled={!data?.analyst_details?.length}
                sx={{
                  textTransform: "none",
                  fontFamily: "Poppins",
                  fontSize: 12,
                  borderColor: "var(--border-1)",
                  color: "var(--text-1)",
                }}
              >
                History
              </Button>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>
    </>
  );
};
